import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface GoalEntry {
  text: string
  progress: number // 0-100
  subGoals: string[]
  completedSubGoals: string[]
  createdAt: number
}

export interface PulseMemory {
  userName: string
  project: string
  goal: string
  goalProgress: number
  goalsByProject: Record<string, GoalEntry>
  stack: string[]
  recentChanges: string[]
  recentFiles: string[] // last 10 files modified (paths)
  lastAction: string // what was the last thing done (for "what's next" suggestions)
  patterns: string[] // learned coding patterns (naming, structure, preferences)
  selfNotes: string[] // self-correction notes (AI writes when user corrects it)
}

interface PulseMemoryState {
  memory: PulseMemory
  setProject: (p: string) => void
  setGoal: (g: string) => void
  setGoalProgress: (pct: number) => void
  addSubGoal: (sub: string) => void
  completeSubGoal: (sub: string) => void
  removeSubGoal: (sub: string) => void
  setStack: (s: string[]) => void
  addStack: (item: string) => void
  removeStack: (item: string) => void
  addRecentChange: (change: string) => void
  addRecentFile: (filePath: string) => void
  setLastAction: (action: string) => void
  removeRecentChange: (change: string) => void
  clearRecentChanges: () => void
  setUserName: (name: string) => void
  addPattern: (pattern: string) => void
  removePattern: (pattern: string) => void
  addSelfNote: (note: string) => void
  removeSelfNote: (note: string) => void
  clear: () => void
}

const DEFAULT: PulseMemory = { userName: '', project: '', goal: '', goalProgress: 0, goalsByProject: {}, stack: [], recentChanges: [], recentFiles: [], lastAction: '', patterns: [], selfNotes: [] }

export const usePulseMemory = create<PulseMemoryState>()(
  persist(
    (set) => ({
      memory: DEFAULT,
      setProject: (p) => set((s) => {
        // When switching projects, load that project's goal
        const entry = s.memory.goalsByProject[p]
        return { memory: { ...s.memory, project: p, goal: entry?.text ?? '', goalProgress: entry?.progress ?? 0 } }
      }),
      setGoal: (g) => set((s) => {
        const proj = s.memory.project
        const existing = s.memory.goalsByProject[proj] ?? { text: '', progress: 0, subGoals: [], completedSubGoals: [], createdAt: Date.now() }
        const entry: GoalEntry = { ...existing, text: g, createdAt: existing.createdAt || Date.now() }
        return { memory: { ...s.memory, goal: g, goalsByProject: { ...s.memory.goalsByProject, [proj]: entry } } }
      }),
      setGoalProgress: (pct) => set((s) => {
        const proj = s.memory.project
        const entry = s.memory.goalsByProject[proj]
        if (!entry) return { memory: { ...s.memory, goalProgress: pct } }
        return { memory: { ...s.memory, goalProgress: pct, goalsByProject: { ...s.memory.goalsByProject, [proj]: { ...entry, progress: pct } } } }
      }),
      addSubGoal: (sub) => set((s) => {
        const proj = s.memory.project
        const entry = s.memory.goalsByProject[proj] ?? { text: s.memory.goal, progress: 0, subGoals: [], completedSubGoals: [], createdAt: Date.now() }
        if (entry.subGoals.includes(sub)) return s
        const updated = { ...entry, subGoals: [...entry.subGoals, sub] }
        return { memory: { ...s.memory, goalsByProject: { ...s.memory.goalsByProject, [proj]: updated } } }
      }),
      completeSubGoal: (sub) => set((s) => {
        const proj = s.memory.project
        const entry = s.memory.goalsByProject[proj]
        if (!entry) return s
        const completedSubGoals = Array.from(new Set([...entry.completedSubGoals, sub]))
        const progress = entry.subGoals.length ? Math.round((completedSubGoals.length / entry.subGoals.length) * 100) : 0
        return { memory: { ...s.memory, goalProgress: progress, goalsByProject: { ...s.memory.goalsByProject, [proj]: { ...entry, completedSubGoals, progress } } } }
      }),
      removeSubGoal: (sub) => set((s) => {
        const proj = s.memory.project
        const entry = s.memory.goalsByProject[proj]
        if (!entry) return s
        const subGoals = entry.subGoals.filter(g => g !== sub)
        const completedSubGoals = entry.completedSubGoals.filter(g => g !== sub)
        const progress = subGoals.length ? Math.round((completedSubGoals.length / subGoals.length) * 100) : 0
        return { memory: { ...s.memory, goalProgress: progress, goalsByProject: { ...s.memory.goalsByProject, [proj]: { ...entry, subGoals, completedSubGoals, progress } } } }
      }),
      setStack: (stack) => set((s) => ({ memory: { ...s.memory, stack } })),
      addStack: (item) => set((s) => ({ memory: { ...s.memory, stack: Array.from(new Set([...s.memory.stack, item])) } })),
      removeStack: (item) => set((s) => ({ memory: { ...s.memory, stack: s.memory.stack.filter(i => i !== item) } })),
      addRecentChange: (change) => set((s) => ({
        memory: { ...s.memory, recentChanges: [change, ...s.memory.recentChanges.filter(c => c !== change)].slice(0, 10) }
      })),
      addRecentFile: (filePath) => set((s) => ({
        memory: { ...s.memory, recentFiles: [filePath, ...s.memory.recentFiles.filter(f => f !== filePath)].slice(0, 10) }
      })),
      setLastAction: (action) => set((s) => ({ memory: { ...s.memory, lastAction: action } })),
      removeRecentChange: (change) => set((s) => ({
        memory: { ...s.memory, recentChanges: s.memory.recentChanges.filter(c => c !== change) }
      })),
      clearRecentChanges: () => set((s) => ({ memory: { ...s.memory, recentChanges: [] } })),
      setUserName: (name) => set((s) => ({ memory: { ...s.memory, userName: name } })),
      addPattern: (pattern) => set((s) => ({ memory: { ...s.memory, patterns: [...s.memory.patterns.filter(p => p !== pattern), pattern].slice(-20) } })),
      removePattern: (pattern) => set((s) => ({ memory: { ...s.memory, patterns: s.memory.patterns.filter(p => p !== pattern) } })),
      addSelfNote: (note) => set((s) => ({ memory: { ...s.memory, selfNotes: [...s.memory.selfNotes.filter(n => n !== note), note].slice(-15) } })),
      removeSelfNote: (note) => set((s) => ({ memory: { ...s.memory, selfNotes: s.memory.selfNotes.filter(n => n !== note) } })),
      clear: () => set({ memory: DEFAULT }),
    }),
    { name: 'pulse-memory-v3', merge: (persisted: any, current: any) => ({ ...current, memory: { ...current.memory, ...(persisted as any)?.memory, patterns: (persisted as any)?.memory?.patterns ?? [], selfNotes: (persisted as any)?.memory?.selfNotes ?? [] } }) }
  )
)
